import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { mealPlans, clients } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import Modal from '../../components/ui/Modal'

const PLAN_TYPES = [
  { value: 'balanced', label: 'Balanced' },
  { value: 'low_carb', label: 'Low Carb' },
  { value: 'high_protein', label: 'High Protein' },
  { value: 'keto', label: 'Keto' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'paleo', label: 'Paleo' },
  { value: 'custom', label: 'Custom' },
]

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' },
  { value: 'pre_workout', label: 'Pre-Workout' },
  { value: 'post_workout', label: 'Post-Workout' },
]

const inputClass = "w-full px-3 py-2 bg-dial-black border border-dial-border rounded-lg text-indices-bright focus:outline-none focus:ring-1 focus:ring-lume-teal"
const labelClass = "block text-sm font-medium text-indices-medium mb-1"
const smallLabelClass = "block text-xs text-indices-muted mb-1"

function MealPlanForm({ plan, onSave, onCancel, isNew }) {
  const [name, setName] = useState(plan?.name || '')
  const [description, setDescription] = useState(plan?.description || '')
  const [mealPlanType, setMealPlanType] = useState(plan?.mealPlanType || 'balanced')
  const [totalCalories, setTotalCalories] = useState(plan?.totalCalories || '')
  const [totalProteinG, setTotalProteinG] = useState(plan?.totalProteinG || '')
  const [totalCarbsG, setTotalCarbsG] = useState(plan?.totalCarbsG || '')
  const [totalFatG, setTotalFatG] = useState(plan?.totalFatG || '')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      name,
      description,
      mealPlanType,
      totalCalories: totalCalories ? parseInt(totalCalories) : null,
      totalProteinG: totalProteinG ? parseFloat(totalProteinG) : null,
      totalCarbsG: totalCarbsG ? parseFloat(totalCarbsG) : null,
      totalFatG: totalFatG ? parseFloat(totalFatG) : null,
      isActive: true,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Name *</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required />
      </div>
      <div>
        <label className={labelClass}>Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>Plan Type</label>
        <select value={mealPlanType} onChange={(e) => setMealPlanType(e.target.value)} className={inputClass}>
          {PLAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div className="border-t border-dial-border pt-4">
        <h3 className="text-sm font-medium text-indices-medium mb-3">Daily Targets (optional)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={smallLabelClass}>Calories</label>
            <input type="number" value={totalCalories} onChange={(e) => setTotalCalories(e.target.value)} placeholder="e.g., 2000" className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Protein (g)</label>
            <input type="number" step="0.1" value={totalProteinG} onChange={(e) => setTotalProteinG(e.target.value)} placeholder="e.g., 150" className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Carbs (g)</label>
            <input type="number" step="0.1" value={totalCarbsG} onChange={(e) => setTotalCarbsG(e.target.value)} placeholder="e.g., 200" className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Fat (g)</label>
            <input type="number" step="0.1" value={totalFatG} onChange={(e) => setTotalFatG(e.target.value)} placeholder="e.g., 65" className={inputClass} />
          </div>
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <button type="button" onClick={onCancel} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium hover:bg-dial-elevated">Cancel</button>
        <button type="submit" className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg hover:opacity-90 font-medium">{isNew ? 'Create Plan' : 'Save Changes'}</button>
      </div>
    </form>
  )
}

function AddMealModal({ planId, onClose, onSuccess }) {
  const [name, setName] = useState('')
  const [mealType, setMealType] = useState('breakfast')
  const [description, setDescription] = useState('')
  const [calories, setCalories] = useState('')
  const [proteinG, setProteinG] = useState('')
  const [carbsG, setCarbsG] = useState('')
  const [fatG, setFatG] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await mealPlans.addMeal(planId, {
        name, mealType,
        description: description || undefined,
        calories: calories || undefined,
        proteinG: proteinG || undefined,
        carbsG: carbsG || undefined,
        fatG: fatG || undefined,
        notes: notes || undefined,
      })
      onSuccess()
    } catch (error) {
      console.error('Failed to add meal:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Add Meal">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelClass}>Meal Name *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Grilled Chicken Salad" className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Meal Type *</label>
          <select value={mealType} onChange={(e) => setMealType(e.target.value)} className={inputClass}>
            {MEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Ingredients or preparation notes" className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={smallLabelClass}>Calories</label>
            <input type="number" value={calories} onChange={(e) => setCalories(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Protein (g)</label>
            <input type="number" step="0.1" value={proteinG} onChange={(e) => setProteinG(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Carbs (g)</label>
            <input type="number" step="0.1" value={carbsG} onChange={(e) => setCarbsG(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Fat (g)</label>
            <input type="number" step="0.1" value={fatG} onChange={(e) => setFatG(e.target.value)} className={inputClass} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Additional notes" className={inputClass} />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium hover:bg-dial-elevated">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg hover:opacity-90 font-medium disabled:opacity-50">{isSubmitting ? 'Adding...' : 'Add Meal'}</button>
        </div>
      </form>
    </Modal>
  )
}

function EditMealModal({ planId, meal, onClose, onSuccess }) {
  const [name, setName] = useState(meal.name || '')
  const [mealType, setMealType] = useState(meal.mealType || 'breakfast')
  const [description, setDescription] = useState(meal.description || '')
  const [calories, setCalories] = useState(meal.calories || '')
  const [proteinG, setProteinG] = useState(meal.proteinG || '')
  const [carbsG, setCarbsG] = useState(meal.carbsG || '')
  const [fatG, setFatG] = useState(meal.fatG || '')
  const [notes, setNotes] = useState(meal.notes || '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await mealPlans.updateMeal(planId, {
        mealId: meal.id, name, mealType,
        description: description || undefined,
        calories: calories || undefined,
        proteinG: proteinG || undefined,
        carbsG: carbsG || undefined,
        fatG: fatG || undefined,
        notes: notes || undefined,
      })
      onSuccess()
    } catch (error) {
      console.error('Failed to update meal:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Edit Meal">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelClass}>Meal Name *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Meal Type *</label>
          <select value={mealType} onChange={(e) => setMealType(e.target.value)} className={inputClass}>
            {MEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputClass} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={smallLabelClass}>Calories</label>
            <input type="number" value={calories} onChange={(e) => setCalories(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Protein (g)</label>
            <input type="number" step="0.1" value={proteinG} onChange={(e) => setProteinG(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Carbs (g)</label>
            <input type="number" step="0.1" value={carbsG} onChange={(e) => setCarbsG(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={smallLabelClass}>Fat (g)</label>
            <input type="number" step="0.1" value={fatG} onChange={(e) => setFatG(e.target.value)} className={inputClass} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass} />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium hover:bg-dial-elevated">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg hover:opacity-90 font-medium disabled:opacity-50">{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </form>
    </Modal>
  )
}

function AssignModal({ planId, onClose, onSuccess }) {
  const [clientId, setClientId] = useState('')
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: clientList } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!clientId) return
    setIsSubmitting(true)
    try {
      await mealPlans.assignToClient(planId, parseInt(clientId), startDate)
      onSuccess()
    } catch (error) {
      console.error('Failed to assign meal plan:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Assign to Client">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelClass}>Client *</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputClass} required>
            <option value="">Select a client</option>
            {clientList?.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Start Date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 px-4 border border-dial-border rounded-lg text-indices-medium hover:bg-dial-elevated">Cancel</button>
          <button type="submit" disabled={isSubmitting || !clientId} className="flex-1 py-2 px-4 bg-lume-teal text-dial-black rounded-lg hover:opacity-90 font-medium disabled:opacity-50">{isSubmitting ? 'Assigning...' : 'Assign'}</button>
        </div>
      </form>
    </Modal>
  )
}

export default function MealPlanDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isNew = !id || id === 'new'
  const [isEditing, setIsEditing] = useState(isNew)
  const [showAddMeal, setShowAddMeal] = useState(false)
  const [showAssign, setShowAssign] = useState(false)
  const [editingMeal, setEditingMeal] = useState(null)

  const { data: plan, isLoading } = useQuery({
    queryKey: ['mealPlan', id],
    queryFn: () => mealPlans.get(id),
    enabled: !isNew,
  })

  const createMutation = useMutation({
    mutationFn: mealPlans.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] })
      navigate(`/meals/${data.id}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => mealPlans.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] })
      queryClient.invalidateQueries({ queryKey: ['mealPlan', id] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => mealPlans.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealPlans'] })
      navigate('/meals')
    },
  })

  const removeMealMutation = useMutation({
    mutationFn: (mealId) => mealPlans.removeMeal(id, mealId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mealPlan', id] }),
  })

  const handleSave = (data) => {
    if (isNew) createMutation.mutate(data)
    else updateMutation.mutate(data)
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this meal plan?')) deleteMutation.mutate()
  }

  const handleRemoveMeal = (mealId) => {
    if (confirm('Remove this meal from the plan?')) removeMealMutation.mutate(mealId)
  }

  const handleExportPdf = async () => {
    try { await mealPlans.exportPdf(id) }
    catch { alert('Failed to export PDF') }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dial-black">
        <PageHeader title="Meal Plan" showBack backTo="/meals" />
        <div className="p-4"><div className="animate-pulse bg-dial-surface rounded-xl p-6 h-48" /></div>
      </div>
    )
  }

  if (!isNew && !plan) {
    return (
      <div className="min-h-screen bg-dial-black">
        <PageHeader title="Meal Plan" showBack backTo="/meals" />
        <div className="p-4 text-center py-12"><p className="text-indices-muted">Meal plan not found</p></div>
      </div>
    )
  }

  if (isEditing) {
    return (
      <div className="min-h-screen bg-dial-black">
        <PageHeader title={isNew ? 'New Meal Plan' : 'Edit Meal Plan'} showBack backTo="/meals" />
        <div className="px-4 py-4">
          <div className="bg-dial-surface rounded-xl p-4 border border-dial-border">
            <MealPlanForm plan={plan} onSave={handleSave} onCancel={() => isNew ? navigate('/meals') : setIsEditing(false)} isNew={isNew} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dial-black">
      <PageHeader
        title={plan.name}
        showBack backTo="/meals"
        actions={
          <div className="flex gap-2">
            <button onClick={handleExportPdf} className="p-2 text-lume-teal hover:opacity-80" title="Export PDF">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button onClick={() => setIsEditing(true)} className="p-2 text-lume-teal hover:opacity-80" title="Edit">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button onClick={handleDelete} className="p-2 text-red-500 hover:text-red-400" title="Delete">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Details Card */}
        <div className="bg-dial-surface rounded-xl p-4 border border-dial-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 text-xs rounded-full bg-dial-elevated text-lume-teal border border-dial-border capitalize">
              {plan.mealPlanType?.replace('_', ' ')}
            </span>
          </div>
          {plan.description && (
            <p className="text-sm text-indices-medium mb-3">{plan.description}</p>
          )}

          {/* Macro Targets */}
          {(plan.totalCalories || plan.totalProteinG || plan.totalCarbsG || plan.totalFatG) && (
            <div className="grid grid-cols-4 gap-2 text-center border-t border-dial-border pt-3">
              {plan.totalCalories && (
                <div>
                  <p className="text-lg font-semibold text-indices-bright">{plan.totalCalories}</p>
                  <p className="text-xs text-indices-muted">cal</p>
                </div>
              )}
              {plan.totalProteinG && (
                <div>
                  <p className="text-lg font-semibold text-red-400">{plan.totalProteinG}g</p>
                  <p className="text-xs text-indices-muted">protein</p>
                </div>
              )}
              {plan.totalCarbsG && (
                <div>
                  <p className="text-lg font-semibold text-blue-400">{plan.totalCarbsG}g</p>
                  <p className="text-xs text-indices-muted">carbs</p>
                </div>
              )}
              {plan.totalFatG && (
                <div>
                  <p className="text-lg font-semibold text-yellow-400">{plan.totalFatG}g</p>
                  <p className="text-xs text-indices-muted">fat</p>
                </div>
              )}
            </div>
          )}

          {/* Assign Button */}
          <button
            onClick={() => setShowAssign(true)}
            className="w-full mt-4 py-2 bg-lume-teal text-dial-black rounded-lg hover:opacity-90 flex items-center justify-center gap-2 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Assign to Client
          </button>
        </div>

        {/* Meals */}
        <div className="bg-dial-surface rounded-xl border border-dial-border">
          <div className="px-4 py-3 border-b border-dial-border flex justify-between items-center">
            <h2 className="font-semibold text-indices-bright">Meals</h2>
            <button onClick={() => setShowAddMeal(true)} className="text-lume-teal hover:opacity-80 text-sm font-medium">+ Add Meal</button>
          </div>
          {plan.meals?.length > 0 ? (
            <div className="divide-y divide-dial-border">
              {plan.meals.map((meal) => (
                <div key={meal.id} className="px-4 py-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 bg-dial-elevated text-indices-muted rounded border border-dial-border capitalize">
                          {meal.mealType?.replace('_', ' ')}
                        </span>
                        <p className="font-medium text-indices-bright">{meal.name}</p>
                      </div>
                      {meal.description && <p className="text-sm text-indices-medium mt-1">{meal.description}</p>}
                      <div className="flex gap-3 mt-1 text-xs text-indices-muted">
                        {meal.calories && <span>{meal.calories} cal</span>}
                        {meal.proteinG && <span>{meal.proteinG}g P</span>}
                        {meal.carbsG && <span>{meal.carbsG}g C</span>}
                        {meal.fatG && <span>{meal.fatG}g F</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditingMeal(meal)} className="p-1.5 text-indices-muted hover:text-lume-teal" title="Edit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button onClick={() => handleRemoveMeal(meal.id)} className="p-1.5 text-indices-muted hover:text-red-500" title="Remove">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-indices-muted mb-3">No meals in this plan yet</p>
              <button onClick={() => setShowAddMeal(true)} className="px-4 py-2 bg-lume-teal text-dial-black rounded-lg text-sm font-medium">Add First Meal</button>
            </div>
          )}
        </div>
      </div>

      {showAddMeal && <AddMealModal planId={id} onClose={() => setShowAddMeal(false)} onSuccess={() => { setShowAddMeal(false); queryClient.invalidateQueries({ queryKey: ['mealPlan', id] }) }} />}
      {showAssign && <AssignModal planId={id} onClose={() => setShowAssign(false)} onSuccess={() => { setShowAssign(false); alert('Meal plan assigned successfully!') }} />}
      {editingMeal && <EditMealModal planId={id} meal={editingMeal} onClose={() => setEditingMeal(null)} onSuccess={() => { setEditingMeal(null); queryClient.invalidateQueries({ queryKey: ['mealPlan', id] }) }} />}
    </div>
  )
}
